import {
    Card,
    CardHeader,
    CardContent,
    Button,
    Grid,
    Box,
    ToggleButton,
    ToggleButtonGroup,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton
} from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import _ from "lodash";
import { timezones } from "../../models/timezones";
import { timeToCallWindow } from "../../tools/timeToCallWindow";

export const dayOfWeekIndexes = {
    0: "SU.",
    1: "MO.",
    2: "TU.",
    3: "WE.",
    4: "TH.",
    5: "FR.",
    6: "SA."
};

export default function CallWindowEditor({ callWindows, onChange, onCancel }) {
    return (
        <Formik
            initialValues={{
                days: [],
                beginTime: { hour: 8, minute: 0 },
                endTime: { hour: 20, minute: 0 }
            }}
            validationSchema={Yup.object({
                days: Yup.array().min(1, "Select at least one day"),
                beginTime: Yup.mixed().test(
                    "time-order",
                    "Begin time must be before end time",
                    function (value) {
                        const start = new Date(0, 0, 0, value.hour, value.minute);
                        const end = new Date(
                            0,
                            0,
                            0,
                            this.parent.endTime.hour,
                            this.parent.endTime.minute
                        );
                        return start < end;
                    }
                )
            })}
            onSubmit={(values, formik) => {
                onChange({
                    target: {
                        name: "callWindows",
                        value: [...(callWindows ?? []), values]
                    }
                });
                formik.resetForm();
            }}
        >
            {(formik) => (
                <Box display="flex" flexDirection="column" gap={2}>

                    {/* CALL WINDOW */}
                    <Card variant="outlined">
                        <CardHeader
                            title="Call Window"
                            slotProps={{
                                title: {
                                    variant: "body2",
                                    color: "textSecondary"
                                }
                            }}
                        />
                        <CardContent>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item>
                                    <ToggleButtonGroup
                                        value={formik.values.days}
                                        onChange={(_, days) =>
                                            formik.setFieldValue("days", days)
                                        }
                                    >
                                        {Object.entries(dayOfWeekIndexes).map(([k, v]) => (
                                            <ToggleButton key={k} value={Number(k)}>
                                                {v}
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                    {formik.touched.days && formik.errors.days && (
                                        <Typography color="error" variant="body2">
                                            {formik.errors.days}
                                        </Typography>
                                    )}
                                </Grid>

                                {["begin", "end"].map((time) => (
                                    <Grid item key={time}>
                                        <TextField
                                            label={`${_.capitalize(time)} Time`}
                                            type="time"
                                            value={`${formik.values[`${time}Time`].hour
                                                .toString()
                                                .padStart(2, "0")}:${formik.values[
                                                    `${time}Time`
                                                ].minute
                                                    .toString()
                                                    .padStart(2, "0")}`}
                                            onChange={(e) =>
                                                formik.setFieldValue(
                                                    `${time}Time`,
                                                    timeToCallWindow(e.target.value)
                                                )
                                            }
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* TIME ZONE */}
                    <Card variant="outlined">
                        <CardHeader
                            title="Time Zone"
                            subheader="Not Recommended"
                            slotProps={{
                                title: {
                                    variant: "body2",
                                    color: "textSecondary"
                                },
                                subheader: {
                                    variant: "body2",
                                    color: "error",
                                    fontStyle: "italic"
                                }
                            }}
                        />
                        <CardContent>
                            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                                <FormControl>
                                    <InputLabel>Time Zone</InputLabel>
                                    <Select
                                        label="Time Zone"
                                        name="tmz.offset"
                                        value={formik?.values?.tmz?.offset || ""}
                                        onChange={(e) => { formik.setFieldValue("tmz.offset", e.target.value)
                                            if (!formik?.values?.tmz?.dst) {
                                                formik.setFieldValue("tmz.dst", false);
                                            }
                                        }}
                                    >
                                        {timezones.map((tmz, i) => (
                                            <MenuItem key={i} value={tmz.offset}>
                                                {tmz.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formik?.values?.tmz?.dst || false}
                                            onChange={(e) => { formik.setFieldValue("tmz.dst", e.target.checked)
                                                if (!formik?.values?.tmz?.offset) {
                                                    formik.setFieldValue("tmz.offset", 0);
                                                }
                                            }}
                                            name="tmz.dst"
                                        />
                                    }
                                    label="Daylight Savings Time?"
                                />
                            </Box>
                        </CardContent>
                    </Card>
                    {/* ACTIONS */}
                    <Box display="flex" justifyContent="flex-end" gap={2}>
                        {onCancel && (
                            <Button variant="outlined" onClick={onCancel}>
                                Cancel
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            onClick={formik.submitForm}
                            disabled={!formik.isValid}
                        >
                            Save
                        </Button>
                    </Box>
                </Box>
            )}
        </Formik>
    );
}
